import { Message, MessageAttachedFile } from 'lib-shared-legacy-types';

/** The attachment types Mim can read, by display label and matching extensions. */
const MIM_SUPPORTED_ATTACHMENT_TYPES = [
  { label: 'PNG', extensions: ['png'] },
  { label: 'JPG', extensions: ['jpg', 'jpeg'] },
  { label: 'GIF', extensions: ['gif'] },
  { label: 'WEBP', extensions: ['webp'] },
  { label: 'PDF', extensions: ['pdf'] },
];

const MIM_SUPPORTED_ATTACHMENT_EXTENSIONS =
  MIM_SUPPORTED_ATTACHMENT_TYPES.flatMap(({ extensions }) => extensions);

/** The supported types as a sentence fragment, e.g. `PNG, JPG, and PDF`. */
export const mimSupportedAttachmentTypes = new Intl.ListFormat('en', {
  type: 'conjunction',
}).format(MIM_SUPPORTED_ATTACHMENT_TYPES.map(({ label }) => label));

/** A message whose attachments may not have been normalized yet. */
type MessageWithRawAttachments = Message & {
  attached_files_v2?: string | MessageAttachedFile[] | null;
};

const parseAttachedFilesV2 = (
  value: MessageWithRawAttachments['attached_files_v2'],
): MessageAttachedFile[] => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
};

const getAttachedFiles = (
  message: MessageWithRawAttachments,
): MessageAttachedFile[] => {
  const attachedFiles = message.attached_files;

  if (attachedFiles) {
    return Array.isArray(attachedFiles) ? attachedFiles : [attachedFiles];
  }

  return parseAttachedFilesV2(message.attached_files_v2);
};

/** The attachment's name, falling back to the last segment of its url. */
const getAttachmentName = (file: MessageAttachedFile): string =>
  file.pretty_name || file.url?.split('/').pop() || '';

const isSupportedAttachment = (name: string): boolean => {
  const lowerCased = name.toLowerCase();
  const extension = lowerCased.split('.').pop();

  return (
    !!extension &&
    extension !== lowerCased &&
    MIM_SUPPORTED_ATTACHMENT_EXTENSIONS.includes(extension)
  );
};

/** The names of the messages' attachments Mim cannot read, matched on extension. */
export const findUnsupportedMimAttachments = (
  messages?: Message[] | null,
): string[] =>
  (messages ?? [])
    .flatMap((message) =>
      getAttachedFiles(message as MessageWithRawAttachments),
    )
    .map(getAttachmentName)
    .filter((name) => !isSupportedAttachment(name));
